import html2canvas from 'html2canvas';
import { TicketPayload, renderTicketPreviewHTML } from '../services/ticketService';
import { openWhatsApp } from './whatsappShare';
import { isNativePlatform } from './platform';

/**
 * Genera una imagen JPG de un ticket y la comparte vía Web Share API o WhatsApp.
 * Retorna true si se compartió con éxito, false si fue cancelado por el usuario.
 */
export async function shareTicketAsImage(payload: TicketPayload): Promise<boolean> {
  const { blob } = await generateTicketImage(payload);
  const fileName = `ticket-${payload.ticketCode}.jpg`;
  const file = new File([blob], fileName, { type: 'image/jpeg' });

  if (navigator.share && navigator.canShare) {
    const shareData = { files: [file], title: `Ticket ${payload.ticketCode}` };
    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }
  }

  // Fallback: descargar + abrir WhatsApp con texto
  const jpgUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = jpgUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(jpgUrl);
  openWhatsApp(`🎟 Ticket: ${payload.ticketCode}\n💰 Total: ₡${payload.total.toLocaleString()}`);
  return true;
}

/**
 * Genera la imagen JPG del ticket y devuelve tanto el Blob como un dataUrl para preview.
 */
export async function generateTicketImage(payload: TicketPayload): Promise<{ blob: Blob; dataUrl: string }> {
  const html = renderTicketPreviewHTML(payload);
  const native = isNativePlatform();

  // Configuración separada APK vs Web
  const anchoPx     = payload.config.ancho === 58
                        ? (native ? 340 : 280)
                        : (native ? 420 : 380);
  const lineHeight  = native ? '1.25'          : '1.35';
  const padding     = native ? '14px 10px 60px' : '6px 4px 60px';
  const scale       = native ? 2.5             : 1.5;
  const jpegQuality = native ? 0.95            : 0.92;

  const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);

  if (!bodyMatch) {
    throw new Error('No se pudo extraer el contenido del ticket');
  }

  // Contenedor temporal off-screen.
  // position:absolute (no fixed) para que scrollHeight refleje el contenido real completo.
  // NO usar visibility:hidden — html2canvas no renderiza elementos con esa propiedad.
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.pointerEvents = 'none';

  const wrapper = document.createElement('div');
  wrapper.style.width = `${anchoPx}px`;
  wrapper.style.maxWidth = `${anchoPx}px`;
  wrapper.style.fontFamily = "'Courier New', Courier, monospace";
  wrapper.style.lineHeight = lineHeight;
  wrapper.style.color = '#000';
  wrapper.style.background = '#fff';
  wrapper.style.padding = padding;
  wrapper.style.textAlign = 'center';
  wrapper.style.boxSizing = 'border-box';

  if (styleMatch) {
    const styleEl = document.createElement('style');
    styleEl.textContent = styleMatch[1];
    container.appendChild(styleEl);
  }

  wrapper.innerHTML = bodyMatch[1].replace(/<script[\s\S]*?<\/script>/gi, '');

  // Eliminar todos los separadores (<hr> y elementos .sep) de la imagen compartida.
  // Esto evita que html2canvas los renderice en posiciones incorrectas sobre el texto.
  wrapper.querySelectorAll('hr, .sep').forEach(el => el.remove());

  // Fix: las etiquetas de .meta-row usan grid-template-columns con ancho fijo (76px).
  // En Courier New, "CLIENTE:" (~77px) puede desbordar esa columna y el ":" cae a la
  // línea siguiente. Se sobreescribe la columna a "auto" solo para la imagen.
  const fixStyle = document.createElement('style');
  fixStyle.textContent = `
    .meta-row { grid-template-columns: auto 1fr !important; }
    .meta-label { white-space: nowrap !important; }
  `;
  container.appendChild(fixStyle);

  // Barcode fallback: reemplazar SVG por canvas con texto
  const barcodeSvg = wrapper.querySelector('#ticket-barcode');
  const barcodeWrap = wrapper.querySelector('.barcode-wrap');
  if (barcodeWrap && !payload.esReimpresion) {
    try {
      const barcodeCanvas = document.createElement('canvas');
      barcodeCanvas.width = anchoPx - 16;
      barcodeCanvas.height = 50;
      const ctx = barcodeCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, barcodeCanvas.width, barcodeCanvas.height);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 14px Courier New';
        ctx.textAlign = 'center';
        ctx.fillText(payload.ticketCode, barcodeCanvas.width / 2, 30);
      }
      if (barcodeSvg) barcodeSvg.remove();
      barcodeWrap.innerHTML = '';
      barcodeWrap.appendChild(barcodeCanvas);
    } catch {
      if (barcodeSvg) barcodeSvg.remove();
    }
  }

  container.appendChild(wrapper);
  document.body.appendChild(container);

  // Esperar a que el layout se calcule completamente
  await new Promise(r => setTimeout(r, 150));

  try {
    // Con position:absolute, html2canvas auto-detecta la altura real del wrapper
    // sin límite de viewport. El padding inferior de 60px garantiza que el pie
    // del ticket no se corte. No se pasa height explícito para evitar desajustes.
    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale,
      width: anchoPx,
      windowWidth: anchoPx,
      scrollX: 0,
      scrollY: 0,
      useCORS: true,
      logging: false,
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => b ? resolve(b) : reject(new Error('Error al generar imagen')),
        'image/jpeg', jpegQuality
      );
    });

    const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    return { blob, dataUrl };
  } finally {
    document.body.removeChild(container);
  }
}
